import { useMemo, useState } from "react";
import { Check, ChevronDown, ChevronRight, Eraser, Layers, Repeat, Search, X } from "lucide-react";
import { procedureColor } from "@/components/treatment/procedureColors";

// =====================================================================
// ProceduresPalette — Doctor view's right-rail palette for the three
// non-Chart modes (Tx Plan / Today / Future Need). Mirrors the
// FindingsPalette dock visually so chart-mode and procedure-mode
// alignment is identical (Cameron: "same alignment for easier
// reference"). Click a procedure → it's armed; PR D wires the
// chart's onToothInterceptClick to consume the armed procedure.
//
// Procedures group by category in an accordion. Default-open
// categories cover the bulk of chairside work (Restorative,
// Diagnostic, Preventive); the rest collapse. Within each
// category procedures sort alphabetically — replace with a
// usage-frequency RPC when there's enough click data to sort by.
// =====================================================================

// Category ordering — top-down by chairside frequency for a typical
// general-practice clinic. Categories not in this list fall to the
// bottom alphabetically.
const CATEGORY_ORDER = [
  "Restorative",
  "Diagnostic",
  "Preventive",
  "Endodontic",
  "Periodontal",
  "Prosthodontic",
  "Oral Surgery",
  "Cosmetic",
  "Pediatric",
  "Orthodontic",
  "Implant",
  "Emergency",
];

const DEFAULT_OPEN_CATEGORIES = new Set(["Restorative", "Diagnostic", "Preventive", "Endodontic", "Oral Surgery"]);

// Mode tone — palette header strip + armed-procedure background
// match the chart's mode-toggle color so the dentist's eye sees
// "I'm in Tx Plan mode" everywhere it matters.
const MODE_TONES = {
  tx_plan:     { ring: "ring-violet-300", bg: "bg-violet-600",  border: "border-violet-300", label: "Tx Plan",     soft: "bg-violet-50  text-violet-700" },
  today:       { ring: "ring-amber-300",  bg: "bg-amber-500",   border: "border-amber-300",  label: "Today",       soft: "bg-amber-50   text-amber-700" },
  future_need: { ring: "ring-blue-300",   bg: "bg-blue-600",    border: "border-blue-300",   label: "Future Need", soft: "bg-blue-50    text-blue-700" },
};

export default function ProceduresPalette({
  procedures = [],
  // Optional id→color map (from buildProcedureColorMap). When
  // provided, swatches use the dark→light ramp within their
  // category; without it we fall back to the category mid-hue.
  colorMap = null,
  armedProcedureId = null,
  onArm,
  // Whole-mouth (non-tooth-specific) handler. When the doctor clicks
  // a procedure that doesn't attach to a tooth (Oral Prophylaxis,
  // Comprehensive Oral Exam, Pano X-ray, etc.), arming-and-waiting-
  // for-a-tooth-click is a dead end — the chart has nothing to
  // paint on. Parent passes this so the click adds the procedure
  // straight into the active mode's surface (Today / Tx Plan /
  // Future Need) with tooth_number = null, surface = null.
  onPickWholeMouth,
  mode = "today",          // "tx_plan" | "today" | "future_need"
  currency = "PHP",
  // Bottom-row tools — Eraser and Multi-Tooth. Mirror the patient-
  // tab Tx Plan palette so chairside reads the same. Eraser toggles
  // the chart's erase-armed state; Multi-Tooth opens the embedded
  // TreatmentTab's dialog. Both are optional — when a handler is
  // null the corresponding button is hidden so non-Tx-Plan modes
  // (Today / Future Need) don't get UI they can't action.
  eraserOn = false,
  onPickEraser = null,
  onPickMultiTooth = null,
  // When the Multi-Tooth Tool is armed AND the dentist has tapped at
  // least one tooth on the chart, the bottom-right slot transforms
  // from "Multi-Tooth" into "Define (N)". Mirrors patient-tab Tx Plan
  // palette so the affordance is identical across views.
  multiToothOn = false,
  multiToothCount = 0,
  onDefineMultiTooth = null,
  // When true (Doctor Today mode, tablet-landscape+), the palette fills
  // the height of its rail cell instead of sizing to its content, and
  // the procedure list becomes the flex scroll area. This lets the chart
  // drive the row height so the palette never sticks out taller than the
  // tooth chart beside it. Mobile keeps the natural 26vh-capped list.
  fillHeight = false,
  // The phone bottom sheet owns the outer shell and available height.
  // This variant removes the nested card chrome, lets the list fill the
  // sheet, and enlarges the primary touch controls.
  mobileSheet = false,
}) {
  const tone = MODE_TONES[mode] ?? MODE_TONES.today;
  const paletteShellClassName = mobileSheet
    ? "flex h-full min-h-0 flex-col gap-2 bg-white"
    : `bg-white rounded-2xl shadow-sm p-3 sm:p-4 space-y-2 ring-1 ${tone.ring}${
        fillHeight ? " min-[900px]:h-full min-[900px]:flex min-[900px]:flex-col" : ""
      }`;
  const scrollingListClassName = mobileSheet
    ? "min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-contain pr-1"
    : `space-y-1.5 max-h-[26vh] overflow-y-auto pr-1${
        fillHeight ? " min-[900px]:flex-1 min-[900px]:min-h-0 min-[900px]:max-h-none" : ""
      }`;
  const procedureButtonSpacing = mobileSheet ? "min-h-11 py-2.5" : "py-1.5";
  const categoryButtonSpacing = mobileSheet ? "min-h-11 py-2" : "py-1.5";
  const toolButtonSpacing = mobileSheet ? "min-h-11 py-2.5" : "py-1.5";

  // Whole-mouth procedures don't arm for a chart click — they fire
  // immediately. Wrap onArm so the parent only deals with one
  // handler shape, and a single click does the right thing for
  // either category.
  function handlePickProcedure(p, event) {
    if (!p) return;
    const armed = armedProcedureId === p.id;
    if (p.tooth_specific === false && onPickWholeMouth) {
      onPickWholeMouth(p);
      return;
    }
    if (armed && isActiveTutorialTarget(event?.currentTarget)) return;
    onArm?.(armed ? null : p.id);
  }

  // Search query — when non-empty, the accordion collapses into a
  // flat alphabetical list of matches across all categories. With
  // 45+ procedures in the standard catalog, finding "Composite" or
  // "Crown Recementation" by typing beats hunting through sections.
  const [query, setQuery] = useState("");

  // Group procedures by category, sorting categories per
  // CATEGORY_ORDER (unknown-category buckets go to the bottom
  // alphabetically), and procedures alphabetically within each.
  const grouped = useMemo(() => {
    const visible = procedures.filter((p) => !p.archived);
    const byCat = new Map();
    for (const p of visible) {
      const cat = p.category || "Other";
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(p);
    }
    const cats = Array.from(byCat.keys()).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return cats.map((cat) => ({
      category: cat,
      items: byCat.get(cat).slice().sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      ),
    }));
  }, [procedures]);

  // Flat search results — match against name, short_code, code, or
  // category so a quick "compo" / "ext" / "rest" all work.
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const visible = procedures.filter((p) => !p.archived);
    const matched = visible.filter((p) => {
      const hay = [p.name, p.short_code, p.code, p.category].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    return matched.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [procedures, query]);

  // Per-category collapsed state. Default-open per the const above;
  // user can toggle individual categories.
  const [openCats, setOpenCats] = useState(() => {
    const init = {};
    grouped.forEach(({ category }) => {
      init[category] = DEFAULT_OPEN_CATEGORIES.has(category);
    });
    return init;
  });

  function toggleCat(cat) {
    setOpenCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  return (
    <div
      className={paletteShellClassName}
      data-testid="doctor-procedures-palette"
      data-tutorial-id="doctor-procedures-palette"
      data-mobile-sheet={mobileSheet ? "true" : undefined}
    >
      {(!mobileSheet || armedProcedureId) ? (
      <div className={`flex items-center gap-2 ${mobileSheet ? "justify-end" : "justify-between"}`}>
        {!mobileSheet ? (
          <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500">
            Procedures · <span className={`px-1.5 py-0.5 rounded text-[10px] ${tone.soft}`}>{tone.label}</span>
          </p>
        ) : null}
        {armedProcedureId ? (
          <button
            type="button"
            onClick={() => onArm?.(null)}
            data-testid="doctor-procedures-clear"
            className={`inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 ${mobileSheet ? "min-h-11 px-3" : ""}`}
          >
            <X className="w-3 h-3" /> Clear
          </button>
        ) : null}
      </div>
      ) : null}

      {grouped.length === 0 ? (
        <p className="text-xs text-slate-400 px-1 py-3">
          No procedures in your catalog yet — add them in Settings → Services.
        </p>
      ) : (
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search procedures…"
            data-testid="doctor-procedures-search"
            className={`w-full rounded-lg border border-slate-200 pl-9 pr-11 focus:outline-none focus:border-slate-400 ${mobileSheet ? "h-11 text-base" : "py-1.5 text-xs"}`}
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="doctor-procedures-search-clear"
              aria-label="Clear search"
              className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 ${mobileSheet ? "right-0 inline-flex h-11 w-11 items-center justify-center" : "right-1.5 p-0.5"}`}
            >
              <X className="w-3 h-3" />
            </button>
          ) : null}
        </div>
      )}

      {searchResults ? (
        <div className={scrollingListClassName} data-testid="doctor-procedures-search-results">
          {searchResults.length === 0 ? (
            <p className="text-xs text-slate-400 px-1 py-3">No matches.</p>
          ) : (
            <ul className="border border-slate-100 rounded-lg py-1 bg-white">
              {searchResults.map((p) => {
                const armed = armedProcedureId === p.id;
                const swatch = procedureColor(p, colorMap);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={(event) => handlePickProcedure(p, event)}
                      data-testid={`doctor-procedure-${p.id}`}
                      data-tutorial-id={`doctor-procedure-${p.id}`}
                      title={p.name}
                      className={`w-full flex items-center justify-between gap-2 px-2.5 ${procedureButtonSpacing} text-sm transition-colors ${
                        armed ? `${tone.bg} text-white` : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className="inline-flex items-center gap-2 truncate text-left min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
                          style={{ backgroundColor: swatch }}
                          aria-hidden
                        />
                        <span className="truncate">
                          {p.name}
                          <span className={`ml-1.5 text-[10px] ${armed ? "text-white/70" : "text-slate-400"}`}>
                            {p.category}
                          </span>
                        </span>
                      </span>
                      {p.visit_count > 1 ? (
                        <span
                          data-testid={`doctor-procedure-${p.id}-visits`}
                          className={`inline-flex items-center gap-0.5 text-[10px] tabular-nums px-1.5 py-0.5 rounded shrink-0 ${
                            armed ? "bg-white/20 text-white" : "bg-violet-50 text-violet-700"
                          }`}
                          title="Multi-visit — patient returns to finish"
                        >
                          <Repeat className="w-2.5 h-2.5" />
                          Multi-visit
                        </span>
                      ) : null}
                      <span className={`text-[11px] shrink-0 ${armed ? "text-white/80" : "text-slate-400"}`}>
                        {p.default_fee_centavos
                          ? formatMoney(p.default_fee_centavos, currency)
                          : (p.short_code || p.code || "")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
      <div className={scrollingListClassName} data-testid="doctor-procedures-list" data-tutorial-id="doctor-procedures-list">
        {grouped.map(({ category, items }) => {
          const open = !!openCats[category];
          return (
            <div key={category} className="border border-slate-100 rounded-lg">
              <button
                type="button"
                onClick={() => toggleCat(category)}
                data-testid={`doctor-procedures-cat-${category.replace(/\s+/g, "-")}`}
                className={`w-full flex items-center justify-between px-2.5 ${categoryButtonSpacing} text-[11px] font-bold tracking-wider uppercase text-slate-600 hover:bg-slate-50`}
                aria-expanded={open}
              >
                <span className="inline-flex items-center gap-1.5">
                  {open ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                  {category}
                </span>
                <span className="text-[10px] font-medium text-slate-400">{items.length}</span>
              </button>
              {open ? (
                <ul className="border-t border-slate-100 py-1">
                  {items.map((p) => {
                    const armed = armedProcedureId === p.id;
                    // Color swatch — uses the position-based colorMap so
                    // each category renders as a dark→light ramp by name
                    // order. Same color the chart will paint when this
                    // procedure is armed; doubles as a legend.
                    const swatch = procedureColor(p, colorMap);
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={(event) => handlePickProcedure(p, event)}
                          data-testid={`doctor-procedure-${p.id}`}
                          data-tutorial-id={`doctor-procedure-${p.id}`}
                          title={p.name}
                          className={`w-full flex items-center justify-between gap-2 px-2.5 ${procedureButtonSpacing} text-sm transition-colors ${
                            armed
                              ? `${tone.bg} text-white`
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span className="inline-flex items-center gap-2 truncate text-left min-w-0">
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
                              style={{ backgroundColor: swatch }}
                              aria-hidden
                            />
                            <span className="truncate">{p.name}</span>
                          </span>
                          {p.visit_count > 1 ? (
                            <span
                              data-testid={`doctor-procedure-${p.id}-visits`}
                              className={`inline-flex items-center gap-0.5 text-[10px] tabular-nums px-1.5 py-0.5 rounded shrink-0 ${
                                armed ? "bg-white/20 text-white" : "bg-violet-50 text-violet-700"
                              }`}
                              title="Multi-visit — patient returns to finish"
                            >
                              <Repeat className="w-2.5 h-2.5" />
                              Multi-visit
                            </span>
                          ) : null}
                          <span className={`text-[11px] shrink-0 ${armed ? "text-white/80" : "text-slate-400"}`}>
                            {p.default_fee_centavos
                              ? formatMoney(p.default_fee_centavos, currency)
                              : (p.short_code || p.code || "")}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
      )}

      {/* Bottom tools — Eraser + Multi-Tooth. Same alignment as the
          patient-tab Tx Plan palette so the dentist's eye lands in the
          same spot in both views. Hidden when no handler is provided
          (Today / Future Need modes don't surface these). */}
      {(onPickEraser || onPickMultiTooth) ? (
        <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-2">
          {onPickEraser ? (
            <button
              type="button"
              onClick={onPickEraser}
              data-testid="doctor-procedures-eraser"
              className={`w-full flex items-center justify-center gap-1.5 px-2 ${toolButtonSpacing} rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-1 ${
                eraserOn
                  ? "border-rose-600 bg-rose-50 text-rose-700"
                  : "border-slate-200 text-slate-700 bg-white hover:border-rose-400 hover:text-rose-600"
              }`}
            >
              <Eraser className="w-3.5 h-3.5" /> Eraser
            </button>
          ) : <span aria-hidden />}
          {onPickMultiTooth ? (
            multiToothOn && multiToothCount > 0 && onDefineMultiTooth ? (
              <button
                type="button"
                onClick={onDefineMultiTooth}
                data-testid="doctor-procedures-multitooth-define"
                title="Define the multi-tooth procedure for the selected teeth"
                className={`w-full flex items-center justify-center gap-1.5 px-2 ${toolButtonSpacing} rounded-lg border border-violet-600 bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1`}
              >
                <Check className="w-3.5 h-3.5" /> Define ({multiToothCount})
              </button>
            ) : (
              <button
                type="button"
                onClick={onPickMultiTooth}
                data-testid="doctor-procedures-multitooth"
                title="Multi-Tooth Tool — Bridge / Splint / RPD / Denture"
                className={`w-full flex items-center justify-center gap-1.5 px-2 ${toolButtonSpacing} rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 ${
                  multiToothOn
                    ? "border-violet-600 bg-violet-50 text-violet-700"
                    : "border-slate-200 text-slate-700 bg-white hover:border-violet-400 hover:text-violet-700"
                }`}
              >
                <Layers className="w-3.5 h-3.5" /> Multi-Tooth
              </button>
            )
          ) : <span aria-hidden />}
        </div>
      ) : null}

      {armedProcedureId ? (
        <p className="text-[11px] text-slate-500 px-1 leading-tight">
          Tap a tooth on the chart to attach this procedure.
        </p>
      ) : (
        <p className="text-[11px] text-slate-400 px-1 leading-tight">
          Pick a procedure, then tap a tooth.
        </p>
      )}
    </div>
  );
}

function formatMoney(centavos, currency) {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : currency === "INR" ? "₹" : currency ? `${currency} ` : "";
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

function isActiveTutorialTarget(element) {
  return Boolean(
    element?.classList?.contains("tutorial-target-pop")
      || element?.classList?.contains("tutorial-target-surface-emphasis")
      || element?.classList?.contains("tutorial-target-guide"),
  );
}
